// © Microsoft Corporation. All rights reserved.

import { ClientSettings, getCurrentFlights, getPageSettings } from "./PageSettings";

import { getCookieInline } from "@msnews/core";

/** Declared cookie consent required function on PCS side. */
export declare function getCookieConsentRequired(): boolean;

let loggingEndpoint;
const Product = {
    mmx: "emmx",
    edge: "spartan",
    edgeChromium: "anaheim",
    hybrid: "spartan",
    hub: "prime",
    microsoftNews: "msnews",
    office: "entnews"
};

/**
 * Logs error to the given endpoint (e.g. otf). ONLY USE FROM INLINE SCRIPTS.
 * @param message - The error message
 * @param appErrorId - The app error id to be logged
 * @param propertyBag - The blob to store additional error information
 */
export function logError(message: string, appErrorId: number, propertyBag: any): void {
    try {
        const settingsObject: ClientSettings = getPageSettings();
        const flightIds: string = getCurrentFlights();
        const params: object = getLoggingParameters(settingsObject);

        let iKey = "";
        if (params && params["apikey"] && params["apikey"] !== "") {
            iKey = getIKeyValue(params["apikey"]);
        }

        const payload: string = getLoggingPayload(appErrorId, message, settingsObject, iKey, propertyBag, flightIds);
        if (params && payload) {
            console.error(payload);

            const url: string = "https://" + loggingEndpoint + "/OneCollector/1.0" + formatParams(params);
            navigator.sendBeacon(url, payload); // fire and forget
        } else {
            console.log("missing parameters in data-client-settings. " + message);
        }
    } catch {
        console.log("error logging the app error. " + message);
    }
}

/**
 * Gets data to set the logging parameters
 * @param settingsObject - The object containing client settings
 */
function getLoggingParameters(settingsObject: ClientSettings): object {
    if (settingsObject) {
        const { pcsInfo, pageGenTime } = settingsObject;
        const uploadTime = new Date(pageGenTime).getTime();
        const isProd: boolean = !pcsInfo || pcsInfo.env === "prod";

        // JSLL endpoints
        loggingEndpoint = isProd ? "browser.events.data.msn.com" : "events-sandbox.data.msn.com";
        const apikey = isProd
            ? "0ded60c75e44443aa3484c42c1c43fe8-9fc57d3f-fdac-4bcf-b927-75eafe60192e-7279"
            : "f8857dedc6f54ca8962cfb713e01e7d7-e9250191-fe0b-446f-95ae-07516262f98c-7028";

        return {
            cors: "true",
            "content-type": "application/x-json-stream",
            "client-id": "NO_AUTH",
            "client-version": "1DS-Web-JS-2.2.2",
            apikey: apikey,
            "upload-time": uploadTime,
            w: "0",
            anoncknm: "app_anon"
        };
    }

    return null;
}

/**
 * Gets data to set the logging payload
 * @param appErrorId - The app error id to be logged
 * @param message - The error message
 * @param iKey - The key for authenticating on 1DS endpoint
 * @param propertyBag - The blob to store additional error information
 * @param flightIds - The flight ids
 */
function getLoggingPayload(appErrorId: number, message: string, settingsObject: ClientSettings, iKey: string, propertyBag: any, flightIds: string): string {
    if (settingsObject) {
        propertyBag = propertyBag || {};
        const { apptype, pagetype, pageGenTime, bundleInfo } = settingsObject;

        propertyBag.pageGenTime = pageGenTime;
        propertyBag.build = bundleInfo && bundleInfo.v;

        const pageProduct = Product[apptype] ? Product[apptype] : apptype;
        const source = apptype + "-" + pagetype;
        const market = document.getElementsByTagName("html")[0].getAttribute("lang");

        let muid = "";
        try {
            const isCookieConsentRequired: boolean = getCookieConsentRequired && typeof getCookieConsentRequired === "function" && getCookieConsentRequired();
            if (!isCookieConsentRequired) {
                muid = getCookieInline("muid");
            }
        } catch {
            console.log("error fetching muid.");
        }

        // Setting RID=AID might not be correct. The more complex logic in the bundles may determine that we have already burned this as a RID
        // and generate a new one different from AID. But it's better than nothing and we cannot see AID on app error reports right now.
        // Most of the time when we are not using app cache these two will be the same.
        const payload = {
            name: "MS.News.Web.AppError",
            time: pageGenTime,
            ver: "4.0",
            iKey: `o:${iKey}`,
            data: {
                baseData: {},
                baseType: "MS.News.Web.Base",
                page: {
                    name: "default",
                    product: pageProduct,
                    type: pagetype
                },
                browser: {
                    clientId: muid
                },
                flight: {
                    id: flightIds
                },
                request: {
                    activityId: settingsObject.aid,
                    requestId: settingsObject.aid
                },
                locale: {
                    mkt: market
                },
                extSchema: {
                    id: appErrorId,
                    source: source,
                    severity: 0,
                    pb: propertyBag,
                    message: message
                }
            }
        };

        return payload ? JSON.stringify(payload) : null;
    }

    return null;
}

/**
 * Format query string parameters
 * @param params - The logging params
 */
function formatParams(params: object): string {
    return "?" + Object
        .keys(params)
        .map(function (key: string): string {
            return key + "=" + encodeURIComponent(params[key]);
        }).join("&");
}

/**
 * Extracts the value of iKey from apikey
 * @param apikey - The value of apikey for 1DS authentication
 */
export function getIKeyValue(apikey: string): string {

    if (apikey) {
        const iKeyIndex = apikey.indexOf("-");
        if (iKeyIndex > 0) {
            return apikey.substring(0, iKeyIndex);
        }
    }

    return "";
}